// JSON-rendered exam diagram (no images): comparison tables, schedules,
// notices. The diagram_spec lives in content JSON; this renders it faithfully.
import React from 'react';

// Format a diagram data point's value + unit into a single label.
// Fixes the "1840dollars per month" bug: no separator, no thousands
// commas, no currency prefix. Rules (minimal, no LLM guessing):
//   - Numbers get thousands separators via toLocaleString.
//   - Units starting with "dollars per " become a "$" prefix + "/ <rest>"
//     suffix so "$1,840 / month" reads correctly.
//   - Units starting with "dollars" (no "per") become a "$" prefix,
//     dropping "dollars".
//   - "percent ..." units render "<value>% <rest>" with no leading space.
//   - Anything else: `<formatted value> <unit>` with a single space.
function _fmtDiagramValue(value, unit) {
  const raw = value == null ? '' : (typeof value === 'number' ? value.toLocaleString() : String(value));
  if (!unit) return raw;
  const u = String(unit).trim();
  if (/^dollars per /i.test(u)) return `$${raw} / ${u.slice(11).trim()}`;
  if (/^dollars\b/i.test(u)) {
    const rest = u.slice(7).trim();
    return rest ? `$${raw} ${rest}` : `$${raw}`;
  }
  if (/^percent\b/i.test(u)) {
    const rest = u.slice(7).trim();
    return rest ? `${raw}% ${rest}` : `${raw}%`;
  }
  return `${raw} ${u}`;
}

export default function DiagramCard({ diagram }) {
  if (!diagram) return null;
  return (
    <div className="border border-slate-300 bg-white" data-testid="exam-diagram">
      {diagram.title && (
        <div className="border-b border-slate-300 bg-slate-700 px-4 py-2.5 text-center">
          <p className="text-sm font-semibold tracking-wide text-white">{diagram.title}</p>
        </div>
      )}
      {diagram.kind === 'table' && (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {diagram.columns.map((col, i) => (
                <th key={i} className="px-3 py-2 text-left font-semibold text-slate-700">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {diagram.rows.map((row, ri) => (
              <tr key={ri} className="border-b border-slate-100 last:border-0">
                {row.map((cell, ci) => (
                  <td key={ci} className={ci === 0 ? 'px-3 py-2 font-medium text-slate-900' : 'px-3 py-2 text-slate-600'}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(diagram.kind === 'bar' || diagram.kind === 'pie') && diagram.data && (
        <div className="space-y-2 px-4 py-4">
          {(() => {
            const total = diagram.data.reduce((s, d) => s + (d.value || 0), 0) || 1;
            const max = Math.max(...diagram.data.map((d) => d.value || 0), 1);
            return diagram.data.map((d, i) => {
              const pct = diagram.kind === 'pie' ? (d.value / total) * 100 : (d.value / max) * 100;
              return (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <span className="w-36 shrink-0 truncate text-slate-700">{d.label}</span>
                  <div className="h-5 flex-1 bg-slate-100">
                    <div className="h-5 bg-blue-900/80" style={{ width: `${Math.max(2, pct)}%` }} />
                  </div>
                  <span className="w-16 shrink-0 text-right font-medium tabular-nums text-slate-900">
                    {_fmtDiagramValue(d.value, diagram.unit)}
                  </span>
                </div>
              );
            });
          })()}
        </div>
      )}
      {diagram.kind === 'line' && diagram.data && (() => {
        const data = diagram.data;
        const max = Math.max(...data.map((d) => d.value || 0), 1);
        const W = 480, H = 190, padL = 32, padR = 18, padT = 16, padB = 30;
        const iw = W - padL - padR, ih = H - padT - padB;
        const x = (i) => padL + (data.length <= 1 ? iw / 2 : (i / (data.length - 1)) * iw);
        const y = (v) => padT + ih - (v / max) * ih;
        const pts = data.map((d, i) => `${x(i)},${y(d.value || 0)}`).join(' ');
        return (
          <div className="px-4 py-4">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={diagram.title || 'line chart'}>
              <line x1={padL} y1={padT + ih} x2={W - padR} y2={padT + ih} stroke="#cbd5e1" strokeWidth="1" />
              <line x1={padL} y1={padT} x2={padL} y2={padT + ih} stroke="#cbd5e1" strokeWidth="1" />
              <polygon points={`${x(0)},${padT + ih} ${pts} ${x(data.length - 1)},${padT + ih}`} fill="rgba(30,58,138,0.08)" />
              <polyline points={pts} fill="none" stroke="#1e3a8a" strokeWidth="2" />
              {data.map((d, i) => {
                const anchor = i === 0 ? 'start' : i === data.length - 1 ? 'end' : 'middle';
                return (
                  <g key={i}>
                    <circle cx={x(i)} cy={y(d.value || 0)} r="3" fill="#1e3a8a" />
                    <text x={x(i)} y={y(d.value || 0) - 7} textAnchor={anchor} fontSize="10" fontWeight="600" fill="#0f172a">{_fmtDiagramValue(d.value, diagram.unit)}</text>
                    <text x={x(i)} y={H - 9} textAnchor={anchor} fontSize="9" fill="#64748b">{d.label}</text>
                  </g>
                );
              })}
            </svg>
          </div>
        );
      })()}
      {diagram.kind === 'table' && diagram.data && !diagram.rows && (
        <table className="w-full text-sm">
          <tbody>
            {diagram.data.map((d, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-0">
                <td className="px-3 py-2 font-medium text-slate-900">{d.label}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-600">{_fmtDiagramValue(d.value, diagram.unit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {diagram.source_note && (
        <div className="border-t border-slate-200 px-4 py-2">
          <p className="text-xs text-slate-500">{diagram.source_note}</p>
        </div>
      )}
      {diagram.notes && diagram.notes.length > 0 && (
        <div className="border-t border-slate-200 bg-amber-50/60 px-4 py-2.5">
          {diagram.notes.map((note, i) => (
            <p key={i} className="text-xs leading-5 text-slate-600">* {note}</p>
          ))}
        </div>
      )}
    </div>
  );
}
